import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { isServerSide } from "@jti/fe-common";
import { useMessaging, useUpdateConsents } from "@jti/rest-api";
import { useFirebaseNotifications } from "../../hooks/useFirebaseNotifications/useFirebaseNotifications";
import { useLocalStorage } from "../../hooks/useStorage";
import { useUser } from "../../hooks/useUser";
import { useConfig } from "../../providers/useConfig";
import { useTranslations } from "../../providers/useTranslations";
import { InfoBanner } from "../InfoBanner";
import { WebNotificationPermissionPopup } from "../WebNotificationPermissionPopup";
const notificationsAcceptedBanner = "isWebPushNotificationsSuccessBannerShown";
const notificationsDeclinedBanner = "isWebPushNotificationsDeclinedBannerShown";
const hideNotificationsPermissionBannerKey = "hideWebPushNotificationPermissionsPopup";
export const WebPushNotifications = () => {
    const [bannerToShow, setBannerToShow] = useState();
    const [isPermissionPopupVisible, setIsPermissionPopupVisible] = useState(false);
    const { subscribeUser } = useMessaging();
    const { updateConsents } = useUpdateConsents();
    const { isUserLoggedIn } = useUser();
    const { webPushLearnMoreSuccessLink = "", webPushLearnMoreDeclineLink = "", webPushConsentId = "" } = useConfig();
    const { setStorage, getStorage, removeStorage } = useLocalStorage();
    const { __ } = useTranslations();
    const declineNotifications = useCallback(async () => {
        setBannerToShow("declinedBanner");
        try {
            await updateConsents({
                consents: [{ id: webPushConsentId, accepted: false }],
            });
        }
        catch (err) {
            console.error("[INFO] Decline notifications consents failed:", err);
        }
    }, [updateConsents, webPushConsentId]);
    const subscribeToNotifications = async (token) => {
        if (!token || !webPushConsentId) {
            throw new Error("Subscription not possible, one of the credentials missing");
        }
        await Promise.all([
            subscribeUser({ token }),
            updateConsents({
                consents: [{ id: webPushConsentId, accepted: true }],
            }),
        ]);
    };
    const { status, requestPermissionAndGetToken, isWebNotificationsEnabled } = useFirebaseNotifications({
        onSuccess: subscribeToNotifications,
    });
    useEffect(() => {
        const isPermissionPopupWasClosed = getStorage(hideNotificationsPermissionBannerKey, null) === true;
        const isLoginPage = !isServerSide() && window.location.href.includes("/login");
        if (!isLoginPage &&
            isWebNotificationsEnabled &&
            isUserLoggedIn &&
            !isPermissionPopupWasClosed &&
            status === "default") {
            setIsPermissionPopupVisible(true);
        }
    }, [getStorage, isUserLoggedIn, isWebNotificationsEnabled, status]);
    const hidePermissionPopup = useCallback(() => {
        setStorage(hideNotificationsPermissionBannerKey, true);
        setIsPermissionPopupVisible(false);
    }, [setStorage]);
    const handleDeclineNotifications = () => {
        hidePermissionPopup();
        declineNotifications();
    };
    const handleAcceptNotifications = () => {
        hidePermissionPopup();
        requestPermissionAndGetToken();
    };
    useEffect(() => {
        if (status === "granted") {
            setBannerToShow("successBanner");
        }
        if (status === "denied") {
            declineNotifications();
        }
        if (status === "error") {
            toast.error(__("web_notifications.subscription.error"));
            removeStorage(hideNotificationsPermissionBannerKey);
        }
    }, [declineNotifications, status]);
    return (_jsxs("div", { "data-testid": "webPushNotifications", children: [_jsx(WebNotificationPermissionPopup, { isOpened: isPermissionPopupVisible, onDecline: handleDeclineNotifications, onAccept: handleAcceptNotifications }), _jsx(InfoBanner, { bannerType: "green", content: __("web_notifications.subscription.success"), ctaLabel: __("web_notifications.subscription.success_cta"), ctaLink: webPushLearnMoreSuccessLink, storageKey: notificationsAcceptedBanner, showInfoBanner: bannerToShow === "successBanner", dataTestId: "acceptedNotificationsBanner" }), _jsx(InfoBanner, { bannerType: "amber", content: __("web_notifications.subscription.declined"), ctaLabel: __("web_notifications.subscription.declined_cta"), ctaLink: webPushLearnMoreDeclineLink, storageKey: notificationsDeclinedBanner, showInfoBanner: bannerToShow === "declinedBanner", dataTestId: "declinedNotificationsBanner" })] }));
};
//# sourceMappingURL=WebPushNotifications.js.map